import axios, { AxiosInstance } from 'axios';
import { KeycloakInstance, KeycloakResourceAccess, KeycloakTokenParsed } from 'keycloak-js';

import { Values } from '../components/configuration/types';
import { ETL_MANAGER_ID } from '../constants/integrationConstants';

export const createAuthHeaders = (token: string, tenant: string) => ({
  Authorization: `Bearer ${token}`,
  'Tenant-ID': tenant,
});

export const updateAuthHeaders = (
  client: AxiosInstance,
  headers: { Authorization: string; 'Tenant-ID': string }
): AxiosInstance => {
  // eslint-disable-next-line no-param-reassign
  client.defaults.headers.common = {
    ...client.defaults.headers.common,
    ...(headers as any),
  };

  return client;
};

export const createAxiosClient = (token: string, tenant: string) => {
  return updateAuthHeaders(axios.create(), createAuthHeaders(token, tenant));
};

/**
 * @link getSessionTimeoutFromRefreshToken
 * @returns {number} - Session Expiration time from Keycloak refresh token as (exp - iat) as number in milliseconds.
 * Return 0 ms if `token` / `exp` / `iat` is not defined.
 */
export const getSessionTimeoutFromRefreshToken = (refreshTokenParsed: KeycloakTokenParsed | undefined): number => {
  if (!refreshTokenParsed) {
    return 0; // something went wrong, logout immediately
  }

  const { exp, iat } = refreshTokenParsed;
  if (!exp || !iat) {
    return 0; // something went wrong, logout immediately
  }

  return (exp - iat) * 1000; // return in millis
};

export const keycloakUserHasPermissions =
  (keycloak: Authenticated['keycloak']) =>
  (
    desiredPermissions: Values<typeof ETL_PERMISSIONS>[] = [],
    requireAllPermissions = true,
    clientName = ETL_MANAGER_ID
  ) => {
    const permissionsPerClient: KeycloakResourceAccess = keycloak.resourceAccess ?? {};

    // get permission for client, remove duplicates.
    const userPermissionsForClient = new Set(permissionsPerClient[clientName]?.roles ?? []);

    if (!desiredPermissions.length && !!userPermissionsForClient.size) {
      return true;
    }

    if (!userPermissionsForClient.size) {
      return false;
    }

    if (!requireAllPermissions) {
      // eslint-disable-next-line no-restricted-syntax
      for (const desiredPermission of desiredPermissions) {
        if (userPermissionsForClient.has(desiredPermission)) {
          return true;
        }
      }

      return false;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const desiredPermission of desiredPermissions) {
      if (!userPermissionsForClient.has(desiredPermission.toString())) {
        return false;
      }
    }

    // all `desiredPermissions` present in `userPermissionsForClient`
    return true;
  };

export interface Pending {
  status: 'pending';
}

export interface Authenticated {
  status: 'authenticated';
  keycloak: KeycloakInstance;
  logout: (options?: { redirectUri: string }) => void;
  // TODO remove logoutUrl and use logout function everywhere
  logoutUrl: string;
  forceUpdateToken: () => void;
  manageAccountUrl: string;
  /**
   * Checks whether user has desired permissions.
   *
   * @param desiredPermissions - permissions to check.
   * @param requireAllPermissions - whether all permissions are required from `desiredPermissions` array.
   * @param clientName - client to which these permissions should be assigned, defaults to {@link ETL_MANAGER_ID}.
   */
  userHasPermissions: (
    desiredPermissions?: ETLPermissionsType[],
    requireAllPermissions?: boolean,
    clientName?: string
  ) => boolean;
  userId: string;
  userName: string;
  timezone: string;
  locale: string;
  sessionIdleTimeDuration: number; // in millis
}

interface NonAuthenticated {
  status: 'non-authenticated';
  redirectUrl: string;
}

export type Authorization = Pending | Authenticated | NonAuthenticated;

export const ETL_PERMISSIONS = {
  /**
   * Roles
   */
  getRolesKeycloak: 'GET_ROLES_KEYCLOAK',
  getRolesWebex: 'GET_ROLES_WEBEXCC',
  getRolesZoomCC: 'GET_ROLES_ZOOMCC',
  getRolesMSTeams: 'GET_ROLES_MSTEAMS',
  getRolesWebexCalling: 'GET_ROLES_WEBEXCC',

  /**
   * Groups
   */
  getTeamsWebexCalling: 'GET_GROUPS_WEBEXCALLING',

  /**
   * Configuration
   */
  getConfiguration: 'GET_CONFIGURATION',
  createConfiguration: 'CREATE_CONFIGURATION',
  updateConfiguration: 'UPDATE_CONFIGURATION',
  deleteConfiguration: 'DELETE_CONFIGURATION',

  /**
   * Events
   */
  getEvents: 'GET_EVENTS',

  /**
   * For backward compatibility
   */
  createSchedule: 'ETL_MANAGER_CREATE_SCHEDULE',
  deleteSchedule: 'ETL_MANAGER_DELETE_SCHEDULE',
  launchTask: 'ETL_MANAGER_LAUNCH_TASK',
} as const;

export type ETLPermissionsType = Values<typeof ETL_PERMISSIONS>;
